import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useMediaUpload = () => {
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const checkUserStatus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        if (user.email === 'joey@towb.in' || user.email === 'admin-test@example.com' || user.email === 'test.admin.api@example.com') {
          setIsAdmin(true);
        }
      }
    };

    checkUserStatus();
  }, []);

  const uploadImages = async (files: FileList): Promise<string[]> => {
    setUploadingImages(true);
    const uploadedUrls: string[] = [];

    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      for (const file of Array.from(files)) {
        // Validate file
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not an image file`);
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          throw new Error(`${file.name} is too large (max 10MB)`);
        }

        // Generate unique filename with proper user-specific folder structure
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = isAdmin ? `admin/display-images/${fileName}` : `${userId}/display-images/${fileName}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('display-assets')
          .upload(filePath, file);

        if (uploadError) {
          throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('display-assets')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      toast({
        title: "Success",
        description: `${uploadedUrls.length} image(s) uploaded successfully!`,
      });

      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: "Upload Error",
        description: error instanceof Error ? error.message : "Failed to upload images",
        variant: "destructive",
      });
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const deleteImage = async (imageUrl: string): Promise<void> => {
    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];

      // Build possible paths for deletion
      const possiblePaths = [
        `admin/display-images/${fileName}`, // Admin path
        `display-images/${fileName}` // Legacy path (for backwards compatibility)
      ];

      // Add user-specific path if userId is available
      if (userId) {
        possiblePaths.unshift(`${userId}/display-images/${fileName}`);
      }

      // Try deleting from all possible paths
      for (const path of possiblePaths) {
        const { error } = await supabase.storage
          .from('display-assets')
          .remove([path]);

        if (!error) {
          // Successfully deleted, break the loop
          break;
        }
      }
    } catch (error) {
      console.warn('Error deleting image:', error);
      // Non-critical error, don't propagate
    }
  };

  return {
    uploadingImages,
    uploadImages,
    deleteImage
  };
};